import type {
  BaseQueryFn,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { retry, createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import config from '@/config'
import { customAxios, isAxiosError } from '@/utils/axiosHelper'
import { ServiceErrorHandler } from '@/connect-types/backend/service'

/** Legacy admin-only vpn-core calls still use this key. Customer device
 * interfaces no longer receive it; those now use Trinity-minted sessions. */
const VPN_API_KEY = 'UtVqSGyQO/cEDfhwqAecIjjDn3joMUUv10hfU4+XWBQ='

interface VpnServer {
  server_id: string
  hostname: string
  location_name: string
  latitude: number
  longitude: number
  status: 'online' | 'offline'
  last_heartbeat: string
  device_count?: number
  active_count?: number
  metadata?: Record<string, unknown>
}

// Raw API response format (different field names)
interface VpnServerApiResponse {
  id: string
  name: string
  hostname: string
  latitude: number
  longitude: number
  status: 'online' | 'offline'
  last_heartbeat: string
  device_count?: number
  active_count?: number
}

interface VpnServersApiResponse {
  servers: VpnServerApiResponse[]
  count: number
  timestamp: string
}

interface VpnDevice {
  serial_number: string
  name: string
  model: string | null
  ip_address: string
  enabled: boolean
  last_seen: string | null
  transfer_rx: string
  transfer_tx: string
  latency_ms: number | null
  is_online: boolean
  created_at: string
  vpn_server_id: string
  vpn_server_hostname: string
}

interface VpnServersResponse {
  success?: boolean
  data: VpnServer[]
  count?: number
  timestamp?: string
}

interface VpnDevicesResponse {
  success: boolean
  data: VpnDevice[]
  pagination?: {
    offset: number
    limit: number
    count: number
  }
}

export interface ServiceHealth {
  name: string
  status?: 'healthy' | 'unhealthy' | 'degraded'
  responseTime?: number
  httpStatus?: number
  // New fields from updated health endpoint
  host?: string
  port?: number
  reachable?: boolean
  latency?: number
}

interface NetworkHealth {
  wireguard: {
    interface: string
    status: 'up' | 'down'
    peers: number
    subnet: string
  }
  iptables: {
    status: string
    rules: {
      nat: number
      forward: number
      httpForwarding: boolean
      radiusForwarding: boolean
    }
  }
}

interface SystemHealth {
  memory: {
    total: number
    used: number
    free: number
    percentage: number
  }
  cpu: {
    loadAverage: {
      '1m': number
      '5m': number
      '15m': number
    }
  }
  disk: {
    size: string
    used: string
    available: string
    percentage: string
  }
}

interface VpnServerHealth {
  status: 'healthy' | 'unhealthy' | 'degraded'
  timestamp: string
  uptime?: number
  version?: string
  environment?: string
  server?: {
    id: string
    hostname: string
    location: string
  }
  services?: ServiceHealth[]
  network?: NetworkHealth
  system?: SystemHealth
  // New fields from updated health endpoint
  components?: {
    database: 'connected' | 'disconnected'
    redis: 'connected' | 'disconnected'
    wireguard: 'initialized' | 'not_initialized'
  }
  wireguard?: {
    peersCount: number
    interface: string
    listenPort: number
  } | null
  devices?: {
    connected: number
    disconnected: number
  }
  backendServices?: {
    services: ServiceHealth[]
    allReachable: boolean
  }
  region?: {
    id: string
    name: string
    latitude?: number
    longitude?: number
  }
}

export interface VpnDeviceHealth {
  reachable: boolean
  authenticated: boolean
  vpnConnected: boolean
  vpn_connected?: boolean
  serial: string
  ip: string
  latency_ms?: number
  deviceInfo?: VpnDeviceInfo
}

export interface VpnDeviceDetails {
  serial_number: string
  name: string
  model: string | null
  ip_address: string
  vpn_server_id: string
  vpn_server_hostname: string
  breaks_from_server?: string
  enabled: boolean
  is_online: boolean
  last_seen: string | null
  transfer_rx: string
  transfer_tx: string
  latency_ms: number | null
  created_at: string
}

export interface VpnDeviceInfo {
  identity: string
  version: string
  board: string
  model: string
  uptime: string
  cpu: string
  memory: string
  architecture: string
  latency: number
}

export interface VpnDeviceLocation {
  serial: string
  latitude: number
  longitude: number
  accuracy?: number
  source?: string
}

export interface VpnHealth {
  success?: boolean
  status: 'healthy' | 'unhealthy' | 'degraded'
  metrics_service?: string
  // Legacy format
  last_collection?: {
    total_devices: number
    online_devices: number
    uptime_seconds: number
  }
  // New format from /api/system/health
  timestamp?: string
  components?: {
    database: 'connected' | 'disconnected'
    redis: 'connected' | 'disconnected'
    wireguard: 'initialized' | 'not_initialized'
  }
  wireguard?: {
    peersCount: number
    interface: string
    listenPort: number
  }
  devices?: {
    connected: number
    disconnected: number
  }
  backendServices?: {
    services: ServiceHealth[]
    allReachable: boolean
  }
  region?: {
    id: string
    name: string
  }
  uptime?: number
}

// TR-069 Dashboard types
interface TR069Dashboard {
  success: boolean
  data: {
    totalDevices: number
    onlineDevices: number
    offlineDevices: number
    byManufacturer: Record<string, number>
    byModel: Record<string, number>
    recentInforms: {
      serial: string
      timestamp: string
      eventCodes: string[]
    }[]
  }
}

interface TR069Device {
  serial: string
  manufacturer: string
  model: string
  firmware: string
  lastInform: string
  status: 'online' | 'offline'
  parameters?: Record<string, any>
}

// Prometheus Metrics types - matches actual API response from /metrics/json
export interface PrometheusMetricValue {
  value: number
  labels: Record<string, string>
  metricName?: string
  exemplar?: any
}

export interface PrometheusMetric {
  help: string
  name: string
  type: 'counter' | 'gauge' | 'histogram' | 'summary'
  values: PrometheusMetricValue[]
  aggregator: 'sum' | 'average' | 'min' | 'max' | 'first' | 'omit'
}

export type PrometheusMetrics = PrometheusMetric[]

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    FetchBaseQueryError
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      let axiosUrl = baseUrl + url
      if (url.startsWith('https://')) {
        axiosUrl = url
      }
      const result = await customAxios({
        url: axiosUrl,
        method,
        data,
        params,
        headers: {
          'X-API-Key': VPN_API_KEY,
          ...headers,
        },
      })
      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if (isAxiosError(err)) {
        const message = err.response?.data
          ? (err.response.data as any).error ||
            (err.response.data as any).message ||
            'Request failed'
          : err.message

        ServiceErrorHandler(
          err?.response?.status || 0,
          'VPN API Error',
          message,
          () => undefined
        )

        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        }
      }

      return {
        error: {
          status: 500,
          data: 'An unexpected error occurred',
        },
      }
    }
  }

const vpnApi = createApi({
  reducerPath: 'vpn',
  keepUnusedDataFor: 60 * 5, // Cache for 5 minutes
  baseQuery: retry(axiosBaseQuery({ baseUrl: `${config.url.vpn}/api` }), {
    maxRetries: 2,
  }),
  tagTypes: ['VpnServers', 'VpnDevices', 'VpnDeviceHealth'],
  endpoints: (builder) => ({
    getVpnServers: builder.query<VpnServersResponse, void>({
      query: () => ({
        url: '/servers',
        method: 'GET',
      }),
      providesTags: ['VpnServers'],
      // Poll every 30 seconds for real-time server status
      pollingInterval: 30000,
      // Transform API response to match expected interface
      transformResponse: (
        response: VpnServersApiResponse
      ): VpnServersResponse => ({
        data: response.servers.map((server) => ({
          server_id: server.id,
          hostname: server.hostname,
          location_name: server.name,
          latitude: server.latitude,
          longitude: server.longitude,
          status: server.status,
          last_heartbeat: server.last_heartbeat,
          device_count: server.device_count,
          active_count: server.active_count,
        })),
        count: response.count,
        timestamp: response.timestamp,
      }),
    }),
    getVpnDevices: builder.query<VpnDevicesResponse, void>({
      query: () => ({
        url: '/devices',
        method: 'GET',
      }),
      providesTags: ['VpnDevices'],
    }),
    getVpnServerDevices: builder.query<VpnDevice[], string>({
      query: () => ({
        url: '/devices',
        method: 'GET',
      }),
      providesTags: (result, error, serverId) => [
        { type: 'VpnDevices', id: serverId },
      ],
      transformResponse: (
        response: VpnDevicesResponse,
        meta,
        serverId: string
      ) => {
        // Filter devices for the specific server
        return response.data.filter(
          (d) => d.vpn_server_id === serverId && d.is_online
        )
      },
    }),
    getVpnServerHealth: builder.query<VpnServerHealth, string>({
      query: (hostname) => ({
        url: `${
          hostname
            ? `https://${hostname.replace(/^https?:\/\//, '').replace(/\/.*$/, '')}`
            : config.url.vpn
        }/health/detailed`,
        method: 'GET',
      }),
    }),
    // TR-069 endpoints
    getTR069Dashboard: builder.query<TR069Dashboard, string>({
      query: (hostname) => ({
        url: `https://${hostname}/api/tr069/dashboard`,
        method: 'GET',
      }),
    }),
    getTR069Devices: builder.query<
      { success: boolean; data: TR069Device[] },
      string
    >({
      query: (hostname) => ({
        url: `https://${hostname}/api/tr069/devices`,
        method: 'GET',
      }),
    }),
    getTR069Device: builder.query<
      { success: boolean; data: TR069Device },
      { hostname: string; serial: string }
    >({
      query: ({ hostname, serial }) => ({
        url: `https://${hostname}/api/tr069/device/${serial}`,
        method: 'GET',
      }),
    }),
    // Prometheus metrics endpoint
    // Note: Uses /metrics/json (not /api/metrics/json) - this is the Prometheus metrics endpoint
    getPrometheusMetrics: builder.query<PrometheusMetrics, string>({
      query: (hostname) => ({
        url: `https://${hostname}/metrics/json`,
        method: 'GET',
      }),
    }),
    getMetricsJson: builder.query<PrometheusMetrics, string>({
      query: (hostname) => ({
        url: `https://${hostname}/metrics/json`,
        method: 'GET',
      }),
    }),
    getServerDevices: builder.query<VpnDevicesResponse, string>({
      query: () => ({
        url: '/devices',
        method: 'GET',
      }),
      transformResponse: (
        response: VpnDevicesResponse,
        meta,
        hostname: string
      ) => {
        // Filter devices for the specific server by hostname
        return {
          ...response,
          data: response.data.filter((d) => d.vpn_server_hostname === hostname),
        }
      },
    }),
    // Overall VPN health endpoint (uses non-API detailed health URL)
    getVpnHealth: builder.query<VpnHealth, void>({
      query: () => ({
        url: `${config.url.vpn}/health/detailed`,
        method: 'GET',
      }),
      // Poll every 30 seconds for real-time updates
      pollingInterval: 30000,
      transformResponse: (response: VpnHealth) => response,
    }),
    // System health endpoint - new format with device counts and backend services
    getVpnSystemHealth: builder.query<VpnHealth, void>({
      query: () => ({
        url: '/system/health',
        method: 'GET',
      }),
      pollingInterval: 30000,
    }),
    // Detailed health endpoint with additional diagnostics
    getVpnDetailedHealth: builder.query<
      VpnHealth & {
        radius?: {
          reachable: boolean
          ip: string
          authTest?: { success: boolean; error?: string }
        }
      },
      void
    >({
      query: () => ({
        url: `${config.url.vpn}/health/detailed`,
        method: 'GET',
      }),
      pollingInterval: 30000,
    }),
    // Per-device endpoints
    getDeviceHealth: builder.query<
      VpnDeviceHealth,
      { serial: string; password?: string }
    >({
      query: ({ serial, password }) => ({
        url: `/device/${serial}/health`,
        method: 'GET',
        params: { refresh: 'true', ...(password ? { password } : {}) },
      }),
      providesTags: (result, error, { serial }) => [
        { type: 'VpnDeviceHealth', id: serial },
      ],
    }),
    getDeviceDetails: builder.query<VpnDeviceDetails, string>({
      query: (serial) => ({
        url: `/device/${serial}`,
        method: 'GET',
      }),
    }),
    getDeviceInfo: builder.query<VpnDeviceInfo, string>({
      query: (serial) => ({
        url: `/device/${serial}/info`,
        method: 'GET',
      }),
    }),
    getDeviceLocation: builder.query<VpnDeviceLocation, string>({
      query: (serial) => ({
        url: `/device/${serial}/location`,
        method: 'GET',
      }),
    }),
    getDeviceIp: builder.query<{ ip: string }, string>({
      query: (serial) => ({
        url: `/device/${serial}/ip`,
        method: 'GET',
      }),
    }),
    getWireguardStats: builder.query<Record<string, unknown>, void>({
      query: () => ({
        url: '/wireguard/stats',
        method: 'GET',
      }),
    }),
    getWireguardPeers: builder.query<Record<string, unknown>, void>({
      query: () => ({
        url: '/wireguard/peers',
        method: 'GET',
      }),
    }),
    // Pin a device to a specific VPN server (manual lock).
    setDeviceServer: builder.mutation<
      {
        success: boolean
        device: {
          serial_number: string
          vpn_server_id: string | null
          vpn_server_hostname: string | null
          selection_method: string | null
          selection_locked_at: string | null
          selection_locked_by: string | null
        }
      },
      { serial: string; server_id: string; locked_by?: string }
    >({
      query: ({ serial, server_id, locked_by }) => ({
        url: `/device/${serial}/server`,
        method: 'POST',
        data: { server_id, locked_by },
      }),
      invalidatesTags: (result, error, { serial }) => [
        { type: 'VpnDeviceHealth', id: serial },
      ],
    }),
    // Clear a device's manual server lock (return to auto mode).
    clearDeviceServer: builder.mutation<
      {
        success: boolean
        device: {
          serial_number: string
          vpn_server_id: string | null
          vpn_server_hostname: string | null
          selection_method: string | null
        }
      },
      { serial: string }
    >({
      query: ({ serial }) => ({
        url: `/device/${serial}/server`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { serial }) => [
        { type: 'VpnDeviceHealth', id: serial },
      ],
    }),
  }),
})

export const {
  useGetVpnServersQuery,
  useGetVpnDevicesQuery,
  useGetVpnServerDevicesQuery,
  useGetVpnServerHealthQuery,
  useGetTR069DashboardQuery,
  useGetTR069DevicesQuery,
  useGetTR069DeviceQuery,
  useGetPrometheusMetricsQuery,
  useGetMetricsJsonQuery,
  useGetServerDevicesQuery,
  useGetVpnHealthQuery,
  useGetVpnSystemHealthQuery,
  useGetVpnDetailedHealthQuery,
  useGetDeviceHealthQuery,
  useGetDeviceDetailsQuery,
  useGetDeviceInfoQuery,
  useGetDeviceLocationQuery,
  useGetDeviceIpQuery,
  useGetWireguardStatsQuery,
  useGetWireguardPeersQuery,
  useSetDeviceServerMutation,
  useClearDeviceServerMutation,
} = vpnApi

export type {
  VpnServer,
  VpnDevice,
  VpnServerHealth,
  VpnHealth,
  VpnDeviceHealth,
  VpnDeviceDetails,
  VpnDeviceInfo,
  VpnDeviceLocation,
  ServiceHealth,
  NetworkHealth,
  SystemHealth,
  TR069Dashboard,
  TR069Device,
}

export default vpnApi
